import React, { useState, useEffect, useMemo } from 'react';
import { Card, Spin, message, Tabs, Row, Col, Button, Form, DatePicker, TimePicker, Select, Radio, Space, Modal } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../utils/auth';
import { PlusOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { SOP_CONSTANTS } from '../../constants/sopConstants';
import { generateUniqueStepCode, generateTemplateCode } from '../../utils/sopUtils';
import dayjs from 'dayjs';
import scheduledSopService from '../../services/scheduledSopService';
import groupService from '../../services/groupService';
import tagService from '../../services/tagService';
import weworkService from '../../services/weworkService';
import { useFixedFooter, useFixedFooterPlaceholder } from '../../hooks/useFixedFooter';

// 引入自定义Hook
import { useScheduledTemplateForm } from '../SopTemplateEdit/hooks/useScheduledTemplateForm';
import { useScheduledStepManagement, parseMessageListFromStep, parseMomentsListFromStep } from '../SopTemplateEdit/hooks/useScheduledStepManagement';
import { useFileUpload } from '../SopTemplateEdit/hooks/useFileUpload';
import { useMessageList } from '../SopTemplateEdit/hooks/useMessageList';

// 引入子组件
import TemplateHeader from '../SopTemplateEdit/components/TemplateHeader';
import StepModal from '../../components/sop/StepModal';
import TemplateForm from '../../components/sop/TemplateForm';
import StepList from '../../components/sop/StepList';
import ExecutionPlan from '../../components/sop/ExecutionPlan';
import ScheduledSopStepExecutions from '../../components/ScheduledSopStepExecutions';

const { Option } = Select;

// 工具函数：处理步骤的消息类型数据
const processMessageStepParams = (step, originalStep, isUnmodifiedStep, setPreviewUrlsState = null) => {
  // 初始化基本结构
  let actionParams = {
    subtype: 'MESSAGE',
    text: "",
    attachments: []
  };

  // 如果有原始数据且步骤未修改，优先使用原始数据
  if (isUnmodifiedStep && originalStep && typeof originalStep.actionParams === 'string' && originalStep.actionParams.trim() !== '') {
    return originalStep.actionParams;
  }

  // 否则，优先使用messageList重建所有数据
  if (step.messageList && step.messageList.length > 0) {
    // 从messageList中提取消息内容，覆盖而不是追加
    step.messageList.forEach(msg => {
      if (msg.type === 'TEXT') {
        actionParams.text = msg.params.content || "";
      } else if (msg.type === 'IMAGE') {
        console.log('ScheduledSopTemplateEdit - 处理IMAGE:', msg.params);
        actionParams.attachments.push({
          msgtype: 'image',
          image: {
            media_id: msg.params.mediaId || '',
            oss_url: msg.params.ossUrl || ''
          }
        });
      } else if (msg.type === 'VIDEO') {
        actionParams.attachments.push({
          msgtype: 'video',
          video: {
            media_id: msg.params.mediaId,
            oss_url: msg.params.ossUrl || msg.params.mediaId_ossUrl || ''
          }
        });
        
        // 如果ossUrl存在，更新预览URL
        const ossUrl = msg.params.ossUrl || msg.params.mediaId_ossUrl || '';
        if (ossUrl && msg.params.mediaId) {
          setPreviewUrlsState && setPreviewUrlsState(prev => ({
            ...prev,
            [msg.params.mediaId]: ossUrl
          }));
        }
      } else if (msg.type === 'FILE') {
        actionParams.attachments.push({
          msgtype: 'file',
          file: {
            media_id: msg.params.mediaId,
            oss_url: msg.params.ossUrl || ''
          }
        });
      } else if (msg.type === 'LINK') {
        actionParams.attachments.push({
          msgtype: 'link',
          link: {
            title: msg.params.title || '',
            url: msg.params.url || '',
            desc: msg.params.description || '',
            picurl: msg.params.picUrl || '',
            pic_oss_url: msg.params.picUrl_ossUrl || ''
          }
        });
      } else if (msg.type === 'MINIPROGRAM') {
        actionParams.attachments.push({
          msgtype: 'miniprogram',
          miniprogram: {
            title: msg.params.title || '',
            appid: msg.params.appid || '',
            page: msg.params.pagepath || '',
            pic_media_id: msg.params.thumbMediaId || '',
            pic_oss_url: msg.params.thumbMediaId_ossUrl || ''
          }
        });
      }
    });

    return JSON.stringify(actionParams);
  }

  // 如果没有messageList，才尝试从原始数据中获取消息内容
  if (typeof step.actionParams === 'string' && step.actionParams.trim() !== '') {
    try {
      return step.actionParams; // 直接返回原始字符串
    } catch (e) {
      console.warn('解析原始actionParams失败:', e);
    }
  } else if (typeof step.actionParams === 'object' && step.actionParams) {
    try {
      return JSON.stringify(step.actionParams); // 直接序列化原对象
    } catch (e) {
      console.warn('序列化actionParams对象失败:', e);
    }
  }

  // 兜底返回
  return JSON.stringify(actionParams);
};

// 工具函数：处理步骤的标签类型数据
const processTagStepParams = (step, originalStep, isUnmodifiedStep) => {
  let actionParams = {
    operation: step.actionType === 'ADD_TAG' ? 'ADD' : 'REMOVE',
    tagId: step.actionParams?.tagId || 0
  };

  // 尝试从原始数据中获取标签内容
  if (typeof step.actionParams === 'string' && step.actionParams.trim() !== '') {
    try {
      const parsed = JSON.parse(step.actionParams);
      if (parsed.tagId) actionParams.tagId = parsed.tagId;
      if (parsed.operation) actionParams.operation = parsed.operation;
    } catch (e) {
      console.warn('解析原始TAG actionParams失败:', e);
    }
  } else if (typeof step.actionParams === 'object' && step.actionParams) {
    if (step.actionParams.tagId) actionParams.tagId = step.actionParams.tagId;
    if (step.actionParams.operation) actionParams.operation = step.actionParams.operation;
  }

  // 如果有原始数据且步骤未修改，优先使用原始数据
  if (isUnmodifiedStep && originalStep && typeof originalStep.actionParams === 'string' && originalStep.actionParams.trim() !== '') {
    return originalStep.actionParams;
  }

  return JSON.stringify(actionParams);
};

// 工具函数：处理步骤的朋友圈类型数据
const processMomentsStepParams = (step, originalStep, isUnmodifiedStep, setPreviewUrlsState = null) => {
  let actionParams = {
    subtype: 'MOMENTS',
    text: { content: "" },
    attachments: []
  };

  // 如果有原始数据且步骤未修改，优先使用原始数据
  if (isUnmodifiedStep && originalStep && typeof originalStep.actionParams === 'string' && originalStep.actionParams.trim() !== '') {
    return originalStep.actionParams;
  }

  // 否则，优先使用momentsList重建所有数据
  if (step.momentsList && step.momentsList.length > 0) {
    // 处理文本内容
    const textItems = step.momentsList.filter(item => item.type === 'MOMENT_TEXT');
    if (textItems.length > 0 && textItems[0].params && textItems[0].params.content) {
      actionParams.text.content = textItems[0].params.content;
    }

    // 处理图片组和单个图片
    // 首先检查是否有图片组
    const imageGroupItems = step.momentsList.filter(item => item.type === 'MOMENT_IMAGE_GROUP');
    if (imageGroupItems.length > 0) {
      // 如果有图片组，将每个图片都添加为单独的附件
      imageGroupItems.forEach(groupItem => {
        if (groupItem.params && groupItem.params.imageGroup) {
          const { mediaIds, ossUrls } = groupItem.params.imageGroup;
          if (mediaIds && mediaIds.length > 0) {
            mediaIds.forEach(mediaId => {
              actionParams.attachments.push({
                msgtype: 'image',
                image: {
                  media_id: mediaId,
                  oss_url: ossUrls[mediaId] || ''
                }
              });
            });
          }
        }
      });
    }

    // 处理单个图片 (非图片组的图片)
    const imageItems = step.momentsList.filter(item => item.type === 'MOMENT_IMAGE');
    imageItems.forEach(item => {
      if (item.params && item.params.mediaId) {
        actionParams.attachments.push({
          msgtype: 'image',
          image: {
            media_id: item.params.mediaId,
            oss_url: item.params.ossUrl || ''
          }
        });
      }
    });

    // 处理视频
    const videoItems = step.momentsList.filter(item => item.type === 'MOMENT_VIDEO');
    videoItems.forEach(item => {
      if (item.params && item.params.mediaId) {
        const ossUrl = item.params.ossUrl || item.params.mediaId_ossUrl || '';
        actionParams.attachments.push({
          msgtype: 'video',
          video: {
            media_id: item.params.mediaId,
            oss_url: ossUrl
          }
        });
        
        // 如果ossUrl存在，更新预览URL
        if (ossUrl) {
          setPreviewUrlsState && setPreviewUrlsState(prev => ({
            ...prev,
            [item.params.mediaId]: ossUrl
          }));
        }
      }
    });

    // 处理链接
    const linkItems = step.momentsList.filter(item => item.type === 'MOMENT_LINK');
    linkItems.forEach(item => {
      if (item.params) {
        actionParams.attachments.push({
          msgtype: 'link',
          link: {
            title: item.params.title || '',
            url: item.params.url || '',
            desc: item.params.description || '',
            picurl: item.params.picUrl || '',
            pic_oss_url: item.params.picOssUrl || ''
          }
        });
      }
    });

    return JSON.stringify(actionParams);
  }

  // 如果没有momentsList，才尝试从原始数据中获取内容
  if (typeof step.actionParams === 'string' && step.actionParams.trim() !== '') {
    try {
      return step.actionParams; // 直接返回原始字符串
    } catch (e) {
      console.warn('解析原始MOMENTS actionParams失败:', e);
    }
  } else if (typeof step.actionParams === 'object' && step.actionParams) {
    try {
      return JSON.stringify(step.actionParams); // 直接序列化原对象
    } catch (e) {
      console.warn('序列化MOMENTS actionParams对象失败:', e);
    }
  }

  // 兜底返回
  return JSON.stringify(actionParams);
};

// 工具函数：创建备用的actionParams
const createFallbackActionParams = (step) => {
  if (step.type === 'MESSAGE' || step.actionType === 'SEND_MESSAGE') {
    return JSON.stringify({
      subtype: 'MESSAGE',
      text: step.name || '默认消息内容',
      attachments: []
    });
  } else if (step.type === 'MOMENTS' || step.actionType === 'SEND_MOMENT') {
    return JSON.stringify({
      subtype: 'MOMENTS',
      text: { content: step.name || '默认朋友圈内容' },
      attachments: []
    });
  } else if (step.type === 'TAG' || ['UPDATE_TAG', 'ADD_TAG', 'REMOVE_TAG'].includes(step.actionType)) {
    return JSON.stringify({
      operation: step.actionType === 'ADD_TAG' ? 'ADD' : 'REMOVE',
      tagId: 1
    });
  }
  return JSON.stringify({});
};

// 工具函数：处理目标规则
const processTargetRules = (formData) => {
  const mainTargetType = formData.targetType || 'CUSTOMER';
  const customerTargetType = formData.customerTargetType || 'TAG';
  const targetIds = formData.targetIds || [];

  const processedTargetRules = [];

  if (mainTargetType === 'CUSTOMER') {
    if (customerTargetType === 'TAG' && targetIds.length > 0) {
      processedTargetRules.push({
        targetType: customerTargetType,
        targetIds: targetIds.map(id => id.toString())
      });
    } else if (customerTargetType === 'ALL') {
      processedTargetRules.push({
        targetType: 'ALL',
        targetIds: []
      });
    }
  } else if (mainTargetType === 'GROUP' && targetIds.length > 0) {
    processedTargetRules.push({
      targetType: 'GROUP',
      targetIds: targetIds.map(id => id.toString())
    });
  }

  return processedTargetRules;
};

// 工具函数：处理步骤数据
const processStepData = (step, index, id, originalSteps, setPreviewUrlsState = null) => {
  // 初始化步骤基础数据
  const stepData = {
    ...(step.id ? { id: step.id } : {}),
    ...(id && id !== 'new' ? { templateId: parseInt(id) } : {}),
    name: step.name,
    type: step.type,
    stepCode: step.stepCode || generateUniqueStepCode(),
    stepOrder: index + 1,
    description: step.description || '',
    conditionExpression: step.conditionExpression || null,
    scheduledDate: step.scheduledDate ? dayjs(step.scheduledDate).format('YYYY-MM-DD') : null,
    scheduledTime: step.scheduledTime ? dayjs(step.scheduledTime).format('HH:mm:ss') : null
  };

  // 设置默认actionType
  if (step.type === 'MESSAGE') {
    stepData.actionType = 'SEND_MESSAGE';
  } else if (step.type === 'MOMENTS') {
    stepData.actionType = 'SEND_MOMENT';
  } else if (step.type === 'TAG') {
    stepData.actionType = 'UPDATE_TAG';
  } else {
    stepData.actionType = 'DEFAULT_ACTION';
  }

  // 如果步骤已经有actionType，优先使用它
  if (step.actionType) {
    stepData.actionType = step.actionType;
  }

  // 处理步骤级别的目标规则
  if (step.targetRules && step.targetRules.length > 0) {
    stepData.targetRules = step.targetRules.map(rule => ({
      id: rule.id, // 如果有ID则保留
      targetType: rule.targetType,
      targetIds: rule.targetIds
    }));
  }

  // 处理步骤级别的发送成员
  if (step.employeeIds && step.employeeIds.length > 0) {
    stepData.employeeIds = step.employeeIds;
  }

  // 查找原始步骤以检查是否有修改
  const originalStep = originalSteps.find(os => os.id === step.id);
  const isUnmodifiedStep = originalStep ? (
    step.type === 'MESSAGE' ?
      !(step.messageList && JSON.stringify(step.messageList) !== JSON.stringify(originalStep.messageList)) :
      step.type === 'MOMENTS' ?
        !(step.momentsList && JSON.stringify(step.momentsList) !== JSON.stringify(originalStep.momentsList)) :
        step.type === 'TAG' ?
          step.actionParams?.tagId === originalStep.actionParams?.tagId :
          true
  ) : false;

  // 根据步骤类型处理actionParams
  try {
    if (step.type === 'MESSAGE') {
      stepData.actionParams = processMessageStepParams(step, originalStep, isUnmodifiedStep, setPreviewUrlsState);
    } else if (step.type === 'TAG') {
      stepData.actionParams = processTagStepParams(step, originalStep, isUnmodifiedStep);
    } else if (step.type === 'MOMENTS') {
      stepData.actionParams = processMomentsStepParams(step, originalStep, isUnmodifiedStep, setPreviewUrlsState);
    } else {
      stepData.actionParams = typeof step.actionParams === 'string' ?
        step.actionParams : JSON.stringify(step.actionParams || {});
    }
  } catch (error) {
    console.error(`处理步骤 ${step.name} 的actionParams时出错:`, error);
    // 使用备用actionParams
    stepData.actionParams = createFallbackActionParams(step);
  }

  // 确保actionParams是字符串类型
  if (!stepData.actionParams) {
    stepData.actionParams = createFallbackActionParams(step);
  } else if (typeof stepData.actionParams !== 'string') {
    try {
      stepData.actionParams = JSON.stringify(stepData.actionParams);
    } catch (error) {
      console.error(`序列化步骤 ${step.name} 的actionParams失败:`, error);
      stepData.actionParams = createFallbackActionParams(step);
    }
  }

  return stepData;
};

const ScheduledSopTemplateEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useAuth();
  const [tags, setTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('timeline');
  const [hasExecutions, setHasExecutions] = useState(false);
  const [previewUrlsState, setPreviewUrlsState] = useState({});
  const [activeTabKey, setActiveTabKey] = useState('sopInfo');
  const [originalSteps, setOriginalSteps] = useState([]);
  const [activating, setActivating] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);

  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();

  // 使用自定义Hook
  const [steps, setSteps] = useState([]);
  const [messageList, setMessageList] = useState([]);

  const {
    template, setTemplate, loading, setLoading, editForm,
    fetchTemplateDetail, isTemplateActive
  } = useScheduledTemplateForm(id, tenant, setHasExecutions, setSteps);

  const {
    currentStep, setCurrentStep,
    isStepModalVisible, setIsStepModalVisible, stepForm,
    handleSaveStep, handleAddStep, handleEditStep,
    handleCopyStep, handleDeleteStep
  } = useScheduledStepManagement(steps, setSteps, messageList, setMessageList, setPreviewUrlsState);

  const {
    fileList, previewUrls, uploading, uploadProgress,
    handleFileUpload, handleRemoveFile
  } = useFileUpload(stepForm);

  const {
    handleAddMessage, handleDeleteMessage
  } = useMessageList(stepForm, currentStep, setCurrentStep);

  // 判断模板是否为激活状态或有执行实例
  const isTemplateDisabled = () => {
    return isTemplateActive() || hasExecutions;
  };

  // 返回上一页
  const handleGoBack = () => {
    navigate('/scheduled-sop-templates');
  };

  // 获取标签列表
  const fetchTags = async () => {
    try {
      const tags = await tagService.getAllTags();
      setTags(tags);
    } catch (error) {
      console.error('获取标签列表失败:', error);
      message.error('获取标签列表失败');
    }
  };

  // 获取客户分组列表
  const fetchGroups = async () => {
    try {
      // 设置较大的分页参数，确保获取足够多的群组数据
      const params = {
        page: 0,
        size: 500  // 获取更多的群组数据
      };
      const groupsData = await groupService.getAllGroups(params);

      // 确保每个分组对象都有 id 和 name 属性
      const formattedGroups = Array.isArray(groupsData)
        ? groupsData.map(group => ({
          id: group.id,
          name: group.name || group.groupName || '未命名群组',
          description: group.description,
          memberCount: group.memberCount,
          status: group.status
        }))
        : [];

      setGroups(formattedGroups);
    } catch (error) {
      console.error('获取客户分组列表失败:', error);
      message.error('获取客户分组列表失败');
    }
  };

  // 获取企业微信成员列表
  const fetchEmployees = async () => {
    try {
      const employeesData = await weworkService.getAllEmployees();
      setEmployees(employeesData.filter(emp => emp.systemStatus === 1)); // 只获取已启用的成员
    } catch (error) {
      console.error('获取企业微信成员列表失败:', error);
      message.error('获取企业微信成员列表失败');
    }
  };

  // 验证SOP模板和步骤
  const validateTemplate = async () => {
    // 验证表单
    await editForm.validateFields();

    if (isTemplateActive()) {
      throw new Error('当前模板处于激活状态，不能修改，请先将其禁用');
    }

    // 验证步骤
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (!step.scheduledDate || !step.scheduledTime) {
        throw new Error(`第${i + 1}步 "${step.name}" 未设置执行日期或时间`);
      }
    }

    return editForm.getFieldsValue();
  };

  // 准备模板数据
  const prepareTemplateData = (formData, tenantId) => {
    return {
      ...(id && id !== 'new' ? { id: parseInt(id) } : {}),
      name: formData.name,
      description: formData.description,
      targetType: formData.targetType,
      triggerType: SOP_CONSTANTS.TriggerType.SCHEDULED,
      code: formData.code || generateTemplateCode('SCHEDULED'),
      status: template?.status || SOP_CONSTANTS.TemplateStatus.DRAFT,
      tenantId: tenantId,
      employeeIds: formData.employeeIds || []
    };
  };

  // 保存模板
  const handleSaveTemplate = async () => {
    try {
      const formData = await validateTemplate();

      // 获取租户信息
      const tenantStr = localStorage.getItem('tenant');
      if (!tenantStr) {
        message.error('未获取到租户信息，请重新登录');
        return;
      }
      const tenantObj = JSON.parse(tenantStr);
      const tenantId = tenantObj.id;

      setSaving(true);

      // 准备请求数据
      const requestData = {
        template: prepareTemplateData(formData, tenantId),
        targetRules: processTargetRules(formData),
        steps: steps.map((step, index) => processStepData(step, index, id, originalSteps, setPreviewUrlsState))
      };

      // 最终检查：确保所有步骤都有有效的actionParams
      const stepsWithoutActionParams = requestData.steps.filter(step => !step.actionParams);
      if (stepsWithoutActionParams.length > 0) {
        console.warn(`发现${stepsWithoutActionParams.length}个步骤缺少必要参数，已自动修复`);
        stepsWithoutActionParams.forEach(step => {
          step.actionParams = createFallbackActionParams(step);
        });
      }

      // 保存模板
      if (!id || id === 'new') {
        // 创建模式
        const result = await scheduledSopService.createScheduledSopWithSteps(requestData, tenantId);
        message.success('创建成功');
        // 如果是新创建的模板，更新URL以反映新ID，但不导航到新页面
        if (result && result.id) {
          navigate(`/scheduled-sop-templates/${result.id}`, { replace: true });
        }
      } else {
        // 更新模式
        await scheduledSopService.updateScheduledSopWithSteps(id, requestData, tenantId);
        message.success('保存成功');
      }

      // 重新加载模板数据以更新UI
      fetchTemplateDetail();
      return true; // 返回成功标识
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败: ' + (error.response?.data?.message || error.message));
      return false; // 返回失败标识
    } finally {
      setSaving(false);
    }
  };

  // 仅保存模板（带弹窗提醒）
  const handleSaveOnly = async () => {
    console.log('handleSaveOnly called, isTemplateActive:', isTemplateActive());
    const success = await handleSaveTemplate();
    console.log('Save result:', success);
    
    // 如果保存成功且模板不是激活状态，弹窗询问是否激活
    if (success === true && !isTemplateActive()) {
      console.log('Showing activate modal');
      setShowActivateModal(true);
    }
  };

  // 保存并激活
  const handleSaveAndActivate = async () => {
    try {
      // 先保存模板
      const success = await handleSaveTemplate();
      
      // 如果保存成功，直接激活
      if (success === true) {
        await handleActivateTemplate();
      }
    } catch (error) {
      console.error('保存并激活失败:', error);
    }
  };

  // 处理激活确认
  const handleActivateConfirm = async () => {
    setShowActivateModal(false);
    await handleActivateTemplate();
  };

  // 激活模板
  const handleActivateTemplate = async () => {
    if (!id || id === 'new') return;
    
    try {
      setActivating(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      await scheduledSopService.activateScheduledSop(id, tenantId);
      message.success('定时推送已激活');
      
      // 重新加载模板数据以更新UI
      fetchTemplateDetail();
    } catch (error) {
      console.error('激活定时推送失败:', error);
      message.error('激活定时推送失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setActivating(false);
    }
  };

  // 禁用模板
  const handleDeactivateTemplate = async () => {
    if (!id || id === 'new') return;
    
    try {
      setActivating(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      await scheduledSopService.deactivateScheduledSop(id, tenantId);
      message.success('定时推送已禁用');
      
      // 重新加载模板数据以更新UI
      fetchTemplateDetail();
    } catch (error) {
      console.error('禁用定时推送失败:', error);
      message.error('禁用定时推送失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setActivating(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchTemplateDetail();
    fetchTags();
    fetchGroups();
    fetchEmployees();

    // 检查是否需要切换到执行情况标签
    if (location.state && location.state.tab === 'executionStatus' && id && id !== 'new') {
      setActiveTabKey('executionStatus');
    }
  }, [id, location.state]);

  // 当模板数据加载后，保存原始步骤数据
  useEffect(() => {
    if (template && template.steps && template.steps.length > 0) {
      setOriginalSteps(JSON.parse(JSON.stringify(template.steps)));
    }
  }, [template]);

  // 处理标签、客户群和员工数据，创建ID到对象的映射
  const tagsMap = useMemo(() => {
    const map = {};
    tags.forEach(tag => {
      map[tag.id] = tag;
    });
    return map;
  }, [tags]);

  const groupsMap = useMemo(() => {
    const map = {};
    groups.forEach(group => {
      map[group.id] = group;
    });
    return map;
  }, [groups]);

  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach(employee => {
      map[employee.id] = employee;
    });
    return map;
  }, [employees]);

  // 确保每个step都有messageList和momentsList
  const processedSteps = steps.map(step => {
    const newStep = { ...step };

    // 确保 type 字段不为空，根据 actionType 推断 type
    if (!newStep.type) {
      if (newStep.actionType === 'SEND_MESSAGE') {
        newStep.type = 'MESSAGE';
      } else if (newStep.actionType === 'SEND_MOMENT') {
        newStep.type = 'MOMENTS';
      } else if (newStep.actionType === 'UPDATE_TAG' || newStep.actionType === 'ADD_TAG' || newStep.actionType === 'REMOVE_TAG') {
        newStep.type = 'TAG';
      } else {
        newStep.type = 'MESSAGE';
      }
    }

    if (newStep.type === 'MESSAGE' && (!newStep.messageList || newStep.messageList.length === 0)) {
      newStep.messageList = parseMessageListFromStep(newStep);
    }
    if (newStep.type === 'MOMENTS' && (!newStep.momentsList || newStep.momentsList.length === 0)) {
      newStep.momentsList = parseMomentsListFromStep(newStep);
    }
    return newStep;
  });

  // 在组件中合适的地方初始化表单值
  useEffect(() => {
    // 如果是新建模板，设置默认的目标类型和空的企业微信成员列表
    if (!id || id === 'new') {
      editForm.setFieldsValue({
        targetType: 'CUSTOMER',
        employeeIds: []
      });
    }
  }, [id, editForm]);

  return (
    <div className="scheduled-sop-template-edit">
      <TemplateHeader
        id={id}
        handleGoBack={handleGoBack}
        handleSaveTemplate={handleSaveOnly}
        saving={saving}
        isTemplateActive={isTemplateActive}
        handleActivateTemplate={handleActivateTemplate}
        handleDeactivateTemplate={handleDeactivateTemplate}
        activating={activating}
        isScheduled={true}
      />

      <Spin spinning={loading} tip="加载中...">
        <div className="template-content">
          <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} style={{ marginBottom: 16 }}>
            <Tabs.TabPane tab="信息" key="sopInfo">
              <Card title="基本信息" style={{ marginBottom: 24 }}>
                <TemplateForm
                  form={editForm}
                  tags={tags}
                  groups={groups}
                  onTargetTypeChange={() => { }}
                  isTemplateActive={isTemplateDisabled()}
                  hideTagSettings={true}
                  isScheduled={true}
                />
              </Card>

              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>内容序列</span>
                  </div>
                }
                style={{ marginBottom: 24 }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddStep}
                  disabled={isTemplateDisabled()}
                  title={isTemplateDisabled() ? "模板已激活或有执行实例，不允许添加步骤" : ""}
                  style={{ marginBottom: 16 }}
                >
                  添加内容
                </Button>
                <StepList
                  steps={processedSteps}
                  onEditStep={handleEditStep}
                  onCopyStep={handleCopyStep}
                  onDeleteStep={handleDeleteStep}
                  isTemplateActive={isTemplateDisabled()}
                  showScheduledTime={true}
                  isScheduled={true}
                  tagsMap={tagsMap}
                  groupsMap={groupsMap}
                  employeeMap={employeeMap}
                />

                {/* 在StepList下方添加一个添加内容按钮 */}
                {steps.length > 0 && (
                  <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={handleAddStep}
                    disabled={isTemplateDisabled()}
                    title={isTemplateDisabled() ? "模板已激活或有执行实例，不允许添加步骤" : ""}
                    style={{
                      marginTop: 16,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >添加内容</Button>
                )}
              </Card>

            </Tabs.TabPane>

            {id && id !== 'new' && (
              <Tabs.TabPane tab="执行情况" key="executionStatus">
                <Card>
                  <ScheduledSopStepExecutions
                    templateId={id}
                  />
                </Card>
              </Tabs.TabPane>
            )}
          </Tabs>
        </div>
      </Spin>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      {/* 固定底部按钮栏 */}
      <div style={fixedFooterStyle}>
        <Space>
          <Button size="middle" onClick={handleGoBack}>
            取消
          </Button>
          <Button
            type="primary"
            onClick={handleSaveOnly}
            loading={saving}
            disabled={isTemplateDisabled()}
            size="middle"
          >
            仅保存
          </Button>
          <Button
            type="primary"
            onClick={handleSaveAndActivate}
            loading={saving || activating}
            disabled={isTemplateDisabled()}
            size="middle"
          >
            保存并激活
          </Button>
        </Space>
      </div>

      <StepModal
        open={isStepModalVisible}
        title={currentStep ? '编辑内容' : '添加内容'}
        currentStep={currentStep}
        form={stepForm}
        messageList={messageList}
        previewUrls={previewUrlsState}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onSave={handleSaveStep}
        onCancel={() => {
          setIsStepModalVisible(false);
          setCurrentStep(null);
          setMessageList([]);
          stepForm.resetFields();
        }}
        onAddMessage={handleAddMessage}
        onDeleteMessage={handleDeleteMessage}
        handleFileUpload={handleFileUpload}
        handleRemoveFile={handleRemoveFile}
        isTemplateActive={isTemplateDisabled()}
        tags={tags}
        isScheduled={true} // 标记为定时SOP步骤
        groups={groups} // 传递客户群列表
        handleUpdatePreviewUrls={(urls) => setPreviewUrlsState(prev => ({ ...prev, ...urls }))}
      />

      {/* 激活确认弹窗 */}
      <Modal
        title="激活聚合群发"
        open={showActivateModal}
        onOk={handleActivateConfirm}
        onCancel={() => setShowActivateModal(false)}
        okText="立即激活"
        cancelText="稍后激活"
        confirmLoading={activating}
      >
        <p>聚合群发已保存成功！是否要立即激活此定时推送？</p>
        <p style={{ color: '#666', fontSize: '14px' }}>
          激活后，系统将按照设置的时间自动执行推送任务。
        </p>
      </Modal>
    </div>
  );
};

export default ScheduledSopTemplateEdit; 